import React, { Profiler, Suspense, useEffect } from 'react'; // Added Suspense
// Optional: import './App.css';  // Keep if you want, but not needed

import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSession, useSessionContext } from '@supabase/auth-helpers-react'; // New import for session hook
import SignupForm from './components/SignupForm';
import LoginForm from './components/LoginForm';
import CompanySetupForm from './components/CompanySetupForm';
import Dashboard from './components/Dashboard';
import WelcomePage from './pages/WelcomePage';
import ApplicationForm from './components/ApplicationForm'; // New import for application form
import EmployerDashboard from './pages/EmployerDashboard'; // New import for Employer Dashboard
import ApplicantDashboard from './pages/ApplicantDashboard'; // Added for Applicant Dashboard
import JobCreate from './components/JobCreate'; // Added for Create Job route
import VideoTestPage from './pages/VideoTestPage'; // New import for the wrapper
import EmployerJobs from './pages/EmployerJobs';
import JobEdit from './components/JobEdit';
import { Link } from 'react-router-dom';
import { Toaster } from 'react-hot-toast'; // Added for global toasts
import InterviewFlow from './components/InterviewFlow'; // NEW: Import for interview entry
import QuestionPage from './components/QuestionPage'; // NEW: Import for single question
import ApplicantJobs from './pages/ApplicantJobs'; // NEW: Import for candidate job search
import SimLinkEntryPage from './pages/SimLinkEntryPage';
import SimDevScreenPage from './pages/SimDevScreenPage';
import EmployerAdminDashboard from './pages/EmployerAdminDashboard';
import SimCreateLinkPage from './pages/SimCreateLinkPage';
import SimulationLibrary from './pages/SimulationLibrary';
import SimAttemptScreenPage from './pages/SimAttemptScreenPage';
import SimReportPage from './pages/SimReportPage';
import EmployerSimResultsPage from './pages/EmployerSimResultsPage';
import BucketSortE2EPage from './pages/BucketSortE2EPage';
import SimulationDashboard from './pages/employer/SimulationDashboard';
import RequestSimulation from './pages/employer/RequestSimulation';
import EmployerBillingPage from './pages/employer/EmployerBillingPage';
import EmployerBuildRequestPage from './pages/employer/EmployerBuildRequestPage';
import EmployerCommerceOrderStatusPage from './pages/employer/EmployerCommerceOrderStatusPage';
import EmployerInvoiceDetailsPage from './pages/employer/EmployerInvoiceDetailsPage';
import PublicBuildRequestBillingPage from './pages/PublicBuildRequestBillingPage';
import PublicBuildRequestPaymentReturnPage from './pages/PublicBuildRequestPaymentReturnPage';
import AiInterviewsWorkspace from './pages/employer/AiInterviewsWorkspace';
import LiveAvatarSpike from './pages/employer/LiveAvatarSpike';
import OwnedAvatarDemo from './pages/employer/OwnedAvatarDemo';
import { isJasonAdminEmail } from './auth/adminAccess';
import { FEATURES } from './config/featureFlags';
import { consumePendingRoutePerf, isPerfDiagnosticsEnabled, logPerfEvent, roundPerfMs } from './perfDiagnostics';

interface PrivateRouteProps {
  requiredType: 'CANDIDATE' | 'EMPLOYER'; // Add more types if needed (e.g., 'ADMIN')
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ requiredType }) => {
  const session = useSession();
  const sessionContext = useSessionContext();
  const location = useLocation();
  const loading =
    typeof sessionContext?.isLoading === 'boolean'
      ? sessionContext.isLoading
      : session === undefined;

  if (loading) {
    return <div>Loading session...</div>; // Improved message
  }

  if (!session) {
    console.log('No session in PrivateRoute - redirecting to login');
    const redirectTo = `${location.pathname}${location.search}${location.hash}`;
    const loginParams = new URLSearchParams({
      type: requiredType === 'EMPLOYER' ? 'employer' : 'candidate',
      redirectTo,
    });
    return <Navigate to={`/login?${loginParams.toString()}`} replace />;
  }

  // Extract userType from metadata for route guarding (uppercase for consistency)
  const userType = (session.user?.app_metadata?.userType || '').toUpperCase(); // FIXED: Uppercase and fallback to ''
  console.log('User type from metadata in PrivateRoute:', userType);

  // Guard: Check if userType matches requiredType
  if (userType !== requiredType.toUpperCase()) { // FIXED: Uppercase requiredType for match
    console.log(`User type mismatch: Expected ${requiredType}, got ${userType}. Redirecting.`);
    return <Navigate to="/unauthorized" replace />; // CHANGED: To /unauthorized for better UX
  }

  const companyId = typeof session.user?.app_metadata?.companyId === 'string'
    ? session.user.app_metadata.companyId
    : '';
  const isAdminRoute = location.pathname === '/employer/admin';
  if (requiredType === 'EMPLOYER' && !companyId && !isAdminRoute) {
    console.warn('[LOGIN_FLOW] Employer route blocked because companyId metadata is missing', {
      userType,
      currentPath: location.pathname,
      targetRoute: '/company-setup',
    });
    return (
      <Navigate
        to="/company-setup"
        replace
        state={{
          setupMessage: 'Your employer account is signed in, but no company is linked yet. Complete company setup before opening the employer dashboard.',
        }}
      />
    );
  }

  // Add path-based logging for debug (optional, can remove later)
  const currentPath = window.location.pathname;
  console.log('URL path in PrivateRoute:', currentPath);

  return <Outlet />;
};

const AdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const session = useSession();

  if (!isJasonAdminEmail(session?.user?.email)) {
    return <Navigate to="/employer/dashboard" replace />;
  }

  return children;
};

const JasonOnlyRoute: React.FC = () => {
  const session = useSession();
  const sessionContext = useSessionContext();
  const location = useLocation();
  const loading =
    typeof sessionContext?.isLoading === 'boolean'
      ? sessionContext.isLoading
      : session === undefined;

  if (loading) {
    return <div>Loading session...</div>;
  }

  if (!session) {
    const redirectTo = `${location.pathname}${location.search}${location.hash}`;
    const loginParams = new URLSearchParams({
      type: 'employer',
      redirectTo,
    });
    return <Navigate to={`/login?${loginParams.toString()}`} replace />;
  }

  if (!isJasonAdminEmail(session.user?.email)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
};

const onProfileRender: React.ProfilerOnRenderCallback = (
  id,
  phase,
  actualDuration,
  baseDuration,
  startTime,
  commitTime
) => {
  if (!isPerfDiagnosticsEnabled()) return;
  logPerfEvent('react_commit', {
    id,
    phase,
    actualDurationMs: roundPerfMs(actualDuration),
    baseDurationMs: roundPerfMs(baseDuration),
    startTimeMs: roundPerfMs(startTime),
    commitTimeMs: roundPerfMs(commitTime),
    pathname: typeof window !== 'undefined' ? window.location.pathname : '',
  });
};

const withPerfProfiler = (id: string, element: React.ReactElement) => (
  <Profiler id={id} onRender={onProfileRender}>
    {element}
  </Profiler>
);

const PerfRouteObserver: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    if (!isPerfDiagnosticsEnabled()) return;
    const pendingRoute = consumePendingRoutePerf(location.pathname);
    if (!pendingRoute) return;

    logPerfEvent('route_transition_commit', {
      pathname: location.pathname,
      source: pendingRoute.source,
      requestDurationMs: pendingRoute.requestDurationMs,
      responseToCommitMs: roundPerfMs(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) - pendingRoute.responseEndedAtMs
      ),
      serverTiming: pendingRoute.serverTiming || {},
      payloadBytes: pendingRoute.payloadBytes || 0,
    });
  }, [location.pathname, location.search]);

  return null;
};

function App() {
  const enableE2ERoutes = import.meta.env.MODE === 'test' || import.meta.env.VITE_PLAYWRIGHT === '1';

  return (
    <Router>
      <PerfRouteObserver />
      <div className="min-h-screen bg-surface-page font-body text-txt-primary">
        <Suspense fallback={<div>Loading routes...</div>}> {/* FIXED: Added Suspense for async handling */}
          <Routes>
            <Route path="/" element={withPerfProfiler('WelcomePage', <WelcomePage />)} />
            <Route path="/signup" element={<SignupForm />} /> {/* Handles ?type=employer or applicant; backend sets user.type */}
            <Route path="/login" element={<LoginForm />} /> {/* Same for login */}
            <Route path="/company-setup" element={<CompanySetupForm />} />
            <Route path="/sim/lnk/:linkToken" element={withPerfProfiler('SimLinkEntryPage', <SimLinkEntryPage />)} />
            <Route path="/sim/dev/:variantCode/:screenCode" element={<SimDevScreenPage />} />
            <Route
              path="/sim/attempts/:attemptId/screens/:screenCode"
              element={withPerfProfiler('SimAttemptScreenPage', <SimAttemptScreenPage />)}
            />
            <Route path="/sim/reports/:reportToken" element={withPerfProfiler('SimReportPage', <SimReportPage />)} />
            <Route
              path="/public/build-requests/billing/:token"
              element={withPerfProfiler('PublicBuildRequestBillingPage', <PublicBuildRequestBillingPage />)}
            />
            <Route
              path="/public/build-requests/payment-return"
              element={withPerfProfiler('PublicBuildRequestPaymentReturnPage', <PublicBuildRequestPaymentReturnPage />)}
            />
            <Route
              path="/simulation-library"
              element={withPerfProfiler('SimulationLibrary', <SimulationLibrary />)}
            />
            {enableE2ERoutes ? <Route path="/__e2e__/bucket-sort" element={<BucketSortE2EPage />} /> : null}

            {/* Protected Employer Routes */}
            <Route element={<PrivateRoute requiredType="EMPLOYER" />}>
              <Route
                path="/employer/dashboard"
                element={
                  FEATURES.SIMULATIONS
                    ? withPerfProfiler('SimulationDashboard', <SimulationDashboard />)
                    : withPerfProfiler('EmployerDashboard', <EmployerDashboard />)
                }
              />
              <Route path="/employer/dashboard/legacy" element={<EmployerDashboard />} />
              <Route
                path="/employer/admin"
                element={
                  <AdminRoute>
                    <EmployerAdminDashboard />
                  </AdminRoute>
                }
              />
              <Route path="/sim/create-link" element={withPerfProfiler('SimCreateLinkPage', <SimCreateLinkPage />)} />
              <Route
                path="/employer/sim/results"
                element={withPerfProfiler('EmployerSimResultsPage', <EmployerSimResultsPage />)}
              />
              <Route
                path="/employer/request-simulation"
                element={withPerfProfiler('RequestSimulation', <RequestSimulation />)}
              />
              <Route
                path="/employer/billing"
                element={withPerfProfiler('EmployerBillingPage', <EmployerBillingPage />)}
              />
              <Route
                path="/employer/build-request"
                element={withPerfProfiler('EmployerBuildRequestPage', <EmployerBuildRequestPage />)}
              />
              <Route
                path="/employer/invoice-details"
                element={withPerfProfiler('EmployerInvoiceDetailsPage', <EmployerInvoiceDetailsPage />)}
              />
              <Route
                path="/billing/return"
                element={withPerfProfiler('EmployerBillingReturnPage', <EmployerCommerceOrderStatusPage variant="return" />)}
              />
              <Route
                path="/billing/cancel"
                element={withPerfProfiler('EmployerBillingCancelPage', <EmployerCommerceOrderStatusPage variant="cancel" />)}
              />
              <Route
                path="/billing/order/:orderId"
                element={withPerfProfiler('EmployerCommerceOrderStatusPage', <EmployerCommerceOrderStatusPage />)}
              />
            </Route>

            {/* Jason-only AI interview workspace/routes. These are dormant and intentionally private. */}
            <Route element={<JasonOnlyRoute />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/employer/ai-interviews" element={withPerfProfiler('AiInterviewsWorkspace', <AiInterviewsWorkspace />)} />
              <Route path="/employer/ai-interviews/spike/live-avatar" element={withPerfProfiler('LiveAvatarSpike', <LiveAvatarSpike />)} />
              <Route path="/employer/ai-interviews/owned-avatar-demo" element={withPerfProfiler('OwnedAvatarDemo', <OwnedAvatarDemo />)} />
              <Route path="/employer/create-job" element={<JobCreate />} />
              <Route path="/employer/jobs" element={<EmployerJobs />} />
              <Route path="/employer/edit-job/:id" element={<JobEdit />} />
              <Route path="/applicant/dashboard" element={<ApplicantDashboard />} />
              <Route path="/applicant/jobs" element={<ApplicantJobs />} />
              <Route path="/jobs/:jobId/apply" element={<ApplicationForm />} />
              <Route path="/interview/:applicationId" element={<InterviewFlow />} />
              <Route path="/interview/:applicationId/question/:questionId" element={<QuestionPage />} />
              <Route path="/video-test" element={<VideoTestPage />} />
            </Route>

            <Route path="/unauthorized" element={
              <div className="min-h-screen flex items-center justify-center px-4">
                <div className="iv-card max-w-xl text-center">
                  <h1 className="text-page-title font-display font-bold text-txt-primary mb-4">Unauthorized</h1>
                  <p className="text-body text-txt-secondary mb-4">You do not have access to this page.</p>
                  <Link to="/login" className="text-txt-link font-medium hover:text-brand-700">Log in as different user</Link>
                </div>
              </div>
            } />

            <Route path="*" element={<Navigate to="/" />} /> {/* Fallback to welcome */}
          </Routes>
        </Suspense>
      </div>
      <Toaster position="top-center" toastOptions={{ duration: 3000 }} /> {/* Added for global toasts */}
    </Router>
  );
}

export default App;
